import { useEffect, useRef, useState } from "react";

import {
  Colors,
  FormGroup,
  Icon,
  InputGroup,
  TextArea,
} from "@blueprintjs/core";
import Uppy from "@uppy/core";

import { getMediaLink } from "@/components/helpers/url-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { RatingScale } from "@/components/pages_logged_in/fulfillment-forms/RatingScaleField";
import { Truncate } from "@/components/pieces/Truncate";
import { Uploader } from "@/components/pieces/uploader";
import { RED, SUBTEXT_COLOR } from "@/css/constants";
import { FormFieldAnswerType } from "@/graphql";
import { ParticipantComponent } from "@/types/programs";
import { QuestionField } from "@/types/surveys";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem, Resolve } from "@/utils/types";

import { MeetingFormField } from "./bookingUtils";

export type UserFile = NonNullable<
  NonNullableArrayItem<NonNullable<QuestionField["answers"]>>["userFile"]
>;

type ParticipantFormField = NonNullableArrayItem<
  NonNullable<ParticipantComponent["survey"]>["formFields"]
>;

type FormFieldType = Resolve<
  (MeetingFormField | Omit<ParticipantFormField, "order">) & {
    required?: boolean;
    formFieldOptions?: MeetingFormField["formFieldOptions"];
    helpText?: string | null;
    order?: number;
  }
>;

interface FileFieldProps {
  formField: FormFieldType;
  submitting: boolean;
  onSubmitReady: (value: string) => void;
  onSubmitError: () => void;
  onValueChange: (data: { hasValue: boolean }) => void;
  initValue?: string;
}

function FileField({
  formField,
  submitting,
  onSubmitReady,
  onSubmitError,
  onValueChange,
  initValue,
}: FileFieldProps) {
  const uppyRef = useRef<Uppy | null>(null);
  const [fileId, setFileId] = useState(initValue);
  const [error, setError] = useState<React.ReactNode | null>(null);

  useEffect(() => {
    setFileId(initValue);
  }, [initValue]);

  useEffect(() => {
    if (!submitting) {
      return;
    }
    const uppy = uppyRef.current;
    setError("");

    if (uppy?.getFiles().length && !fileId) {
      uppy.upload().then((result) => {
        if (result?.failed?.length) {
          const response = result?.failed?.[0]?.response as ExplicitAny;
          const message = response?.responseText;
          uppy.cancelAll();
          setError(<UploadError message={message} />);
          onSubmitError();
        } else {
          const responseBody = result?.successful?.[0]?.response
            ?.body as Record<string, ExplicitAny>;
          const newFileId = responseBody?.user_file?.id || "";
          // Save it in case of errors
          setError(null);
          setFileId(newFileId);
          onSubmitReady(newFileId);
        }
      });
    } else {
      if (fileId) {
        onSubmitReady(fileId);
      } else if (!formField.required) {
        onSubmitReady("");
      } else {
        uppy?.clear();
        setError(<UploadError message="Please select a file" />);
        onSubmitError();
      }
    }
  }, [submitting]);

  const onUppyFileChanges = () => {
    // Reset fileId when file is changed
    setFileId(undefined);
    const uppy = uppyRef.current;

    console.log("uppy files", uppy?.getFiles());
    onValueChange({
      hasValue: Boolean(uppy && uppy.getFiles().length),
    });
  };

  useEffect(() => {
    return () => {
      uppyRef.current = null;
    };
  }, []);

  return (
    <>
      <Uploader
        endpoint={`${API_HOST}/files`}
        fieldName={`field-${formField.id}`}
        allowMultipleUploads={false}
        uppyDashboardProps={{
          hideUploadButton: true,
        }}
        onFileAdded={onUppyFileChanges}
        onFileRemoved={onUppyFileChanges}
        uppyRef={(uppy: Uppy | null) => {
          uppyRef.current = uppy;
        }}
        onError={() => {
          onSubmitError();
        }}
      />
      {error}
    </>
  );
}

interface TextFieldProps {
  formField: FormFieldType;
  submitting: boolean;
  onSubmitReady: (value: string) => void;
  onValueChange: (data: { hasValue: boolean }) => void;
  placeholder?: string;
  initValue?: string;
  disabled?: boolean;
}

function TextField({
  formField,
  submitting,
  onSubmitReady,
  onValueChange,
  placeholder = "",
  initValue = "",
  disabled = false,
}: TextFieldProps) {
  const [value, setValue] = useState(initValue);
  const { answerType } = formField;
  useEffect(() => {
    if (submitting) {
      onSubmitReady(value);
    }
  }, [submitting]);

  useEffect(() => {
    setValue(initValue);
  }, [initValue]);

  if (
    [
      FormFieldAnswerType.SingleLine,
      FormFieldAnswerType.Linkedin,
      FormFieldAnswerType.Url,
    ].includes(answerType)
  ) {
    return (
      <InputGroup
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          onValueChange({
            hasValue: e.currentTarget.value.length > 0,
          });
        }}
        disabled={submitting || disabled}
        name={`field-${formField?.id}`}
        large={true}
        fill={true}
        placeholder={placeholder}
        leftIcon={answerType === FormFieldAnswerType.Url ? "link" : undefined}
      />
    );
  }
  if (answerType === FormFieldAnswerType.MultiLine) {
    return (
      <TextArea
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          onValueChange({
            hasValue: e.currentTarget.value.length > 0,
          });
        }}
        name={`field-${formField.id}`}
        disabled={submitting || disabled}
        large={true}
        fill={true}
        rows={3}
        autoResize={true}
        placeholder={placeholder}
      />
    );
  }

  return null;
}

interface RatingScaleFieldProps {
  formField: FormFieldType;
  submitting: boolean;
  onSubmitReady: (value: string) => void;
  onValueChange: (data: { hasValue: boolean }) => void;
}

function RatingScaleField({
  formField,
  submitting,
  onSubmitReady,
  onValueChange,
}: RatingScaleFieldProps) {
  const [value, setValue] = useState("");
  useEffect(() => {
    if (submitting) {
      onSubmitReady(value);
    }
  }, [submitting]);

  const formFieldOptions = ensureNonNullArray(formField.formFieldOptions);

  return (
    <RatingScale
      formFieldOptions={formFieldOptions}
      value={value}
      onSelect={(newValue) => {
        setValue(newValue);
        onValueChange({
          hasValue: newValue.length > 0,
        });
      }}
      className="small-rating-scale"
    />
  );
}

interface FormFieldProps {
  formField: FormFieldType;
  submitting: boolean;
  onSubmitReady: (value: string) => void;
  onSubmitError: () => void;
  onValueChange: (data: { hasValue: boolean }) => void;
  placeholder?: string;
  initValue?: string;
  userFile?: UserFile;
  disabled?: boolean;
}

export function FormField({
  formField,
  submitting,
  onSubmitReady,
  onSubmitError,
  onValueChange,
  placeholder = "",
  initValue,
  userFile,
  disabled = false,
}: FormFieldProps) {
  if (
    [
      FormFieldAnswerType.SingleLine,
      FormFieldAnswerType.MultiLine,
      FormFieldAnswerType.Linkedin,
      FormFieldAnswerType.Url,
    ].includes(formField.answerType)
  ) {
    if (formField.answerType === FormFieldAnswerType.Url && initValue) {
      const urlInfo = JSON.parse(initValue);
      initValue = submitting ? urlInfo?.text : urlInfo.url;
    }
    return (
      <TextField
        formField={formField}
        submitting={submitting}
        onSubmitReady={onSubmitReady}
        onValueChange={onValueChange}
        placeholder={placeholder}
        initValue={initValue}
        disabled={disabled}
      />
    );
  } else if (
    [FormFieldAnswerType.File, FormFieldAnswerType.Resume].includes(
      formField.answerType,
    )
  ) {
    let extraComponent: React.ReactNode | null = null;

    if (userFile) {
      extraComponent = (
        <Flex alignItems="center" mb={2}>
          <Icon icon="paperclip" color={SUBTEXT_COLOR} />
          <Box ml={1}>
            <a href={getMediaLink(userFile.url)} target="_BLANK">
              <Truncate>{userFile.name}</Truncate>
            </a>
          </Box>
        </Flex>
      );
    }
    return (
      <>
        {extraComponent}
        <FileField
          formField={formField}
          submitting={submitting}
          onSubmitReady={onSubmitReady}
          onSubmitError={onSubmitError}
          onValueChange={onValueChange}
          initValue={initValue}
        />
      </>
    );
  } else if ([FormFieldAnswerType.RatingScale].includes(formField.answerType)) {
    return (
      <RatingScaleField
        formField={formField}
        submitting={submitting}
        onSubmitReady={onSubmitReady}
        onValueChange={onValueChange}
      />
    );
  } else if ([FormFieldAnswerType.Task].includes(formField.answerType)) {
    return <></>;
  }

  return <Box>This field type is not supported yet</Box>;
}

interface AdditionalInfoFormProps {
  formFields: FormFieldType[];
  submitting: boolean;
  onSubmitReady: (value: { [key: string]: string }) => void;
  onSubmitError: () => void;
  onValueChange: (fieldId: string, hasValue: boolean) => void;
}

export function AdditionalInfoForm({
  formFields,
  submitting,
  onSubmitReady,
  onSubmitError,
  onValueChange,
}: AdditionalInfoFormProps) {
  const [fieldValues, setFieldValues] = useState({});
  const setFieldValue = (fieldId: string, value: string) => {
    setFieldValues((prevData) => ({
      ...prevData,
      [fieldId]: value,
    }));
  };

  // What we are doing here is waiting until all of the
  // form fields have provided this component with their
  // current value. When all fields are accounted for, submit.
  useEffect(() => {
    if (!submitting) return;
    if (Object.keys(fieldValues).length === formFields.length) {
      onSubmitReady(fieldValues);
    }
  }, [fieldValues]);

  useEffect(() => {
    if (!submitting && Object.keys(fieldValues).length > 0) {
      setFieldValues({});
    }
  }, [submitting]);

  return (
    <Box maxWidth={550}>
      {formFields.map((formField) => (
        <FormGroup
          key={formField.id}
          label={<strong>{formField.prompt}</strong>}
          labelFor={`field-${formField.id}`}
          labelInfo={
            formField.required ? (
              <span style={{ color: Colors.RED3 }}>(required)</span>
            ) : (
              "(optional)"
            )
          }
          helperText={formField.helpText}
        >
          <FormField
            formField={formField}
            submitting={submitting}
            onSubmitReady={(value) => setFieldValue(formField.id, value)}
            onSubmitError={() => {
              setFieldValues({});
              onSubmitError();
            }}
            onValueChange={(data) =>
              onValueChange(formField.id, data?.hasValue)
            }
          />
        </FormGroup>
      ))}
    </Box>
  );
}

function UploadError({ message }: { message?: string }) {
  return (
    <Box color={RED} my={3}>
      {message || (
        <>
          We ran into an uploading your file. Please refresh and try again. If
          the issue persists, please contact support.
        </>
      )}
    </Box>
  );
}
